/**
 * Chart arithmetic: linear scales and human-friendly tick selection (D-087).
 *
 * `d3-scale` and `d3-shape` were measured at 16.3 kB gzipped and dropped from
 * the pinned stack. The part worth owning is here; the part worth *not* having
 * is curve smoothing, which would draw weight values on days that were never
 * measured — the same error as treating a missing value as a zero, in the one
 * place a user would believe it.
 *
 * Pure, like every other calculation: no clock, no storage, no formatting. The
 * layering guard enforces it. Components turn this output into path strings.
 */

export interface Scale {
  /** Map a domain value onto the pixel range. */
  readonly at: (value: number) => number
  readonly domain: readonly [number, number]
  readonly range: readonly [number, number]
}

/**
 * A linear scale.
 *
 * A zero-width domain — one observation, or several identical ones — maps
 * everything to the middle of the range rather than dividing by zero. A single
 * weigh-in is a real state this app reaches on day one.
 */
export function linearScale(
  domain: readonly [number, number],
  range: readonly [number, number],
): Scale {
  const [d0, d1] = domain
  const [r0, r1] = range
  const span = d1 - d0

  return {
    at: (value) => (span === 0 ? (r0 + r1) / 2 : r0 + ((value - d0) / span) * (r1 - r0)),
    domain,
    range,
  }
}

/**
 * Ticks at 1, 2, 2.5 or 5 times a power of ten, covering the domain.
 *
 * The forty lines that justify not taking a dependency. The step is chosen from
 * a fixed set of human-friendly multiples so labels read as 79, 79.5, 80 rather
 * than 79.14, 79.63, 80.12 — the difference between a chart someone can read at
 * a glance and one they have to decode.
 *
 * `count` is a target, not a promise: the returned array is whatever that step
 * produces across the domain, which is how every implementation of this works.
 */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return []
  if (count < 1) return []

  if (min === max) return [min]
  if (min > max) return niceTicks(max, min, count)

  const rawStep = (max - min) / count
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalized = rawStep / magnitude

  /* Ordered, so the first match is the smallest multiple that is still at
     least the raw step — which keeps the tick count at or below the target
     rather than overshooting it. */
  const multiple = [1, 2, 2.5, 5, 10].find((candidate) => candidate >= normalized) ?? 10
  const step = multiple * magnitude

  const first = Math.ceil(min / step) * step
  const ticks: number[] = []

  /* Accumulate by multiplication rather than by repeated addition: adding a
     step like 0.1 forty times drifts into 3.9999999999999996, which then
     formats as an absurd label. */
  for (let index = 0; first + index * step <= max + step / 1e6; index += 1) {
    ticks.push(round(first + index * step, step))
  }

  return ticks
}

/**
 * Round to the precision the step implies.
 *
 * A step of 0.5 must not produce 79.50000000000001. This is arithmetic
 * cleanup, not display rounding — D-041 keeps formatted values out of
 * calculations, and this returns a number.
 */
function round(value: number, step: number): number {
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1)
  return Number(value.toFixed(Math.min(decimals, 10)))
}

/**
 * Pad a domain so the extremes are not drawn on the frame.
 *
 * A weight series spanning 78.4–79.6 kg drawn edge to edge puts the highest and
 * lowest points exactly on the border, where they read as clipped. The padding
 * is a fraction of the span, and a zero span gets an absolute pad so a single
 * observation still has a chart to sit in.
 */
export function paddedDomain(
  min: number,
  max: number,
  fraction = 0.08,
  fallbackPad = 1,
): [number, number] {
  if (min === max) return [min - fallbackPad, max + fallbackPad]
  const pad = (max - min) * fraction
  return [min - pad, max + pad]
}

export interface SeriesPoint {
  /** Days from the series origin. Integers, but not required to be contiguous. */
  readonly x: number
  readonly y: number
}

/**
 * Split a series wherever observations are more than `maxGapDays` apart.
 *
 * **This is the reason the charts are hand-rolled.** A straight line drawn
 * across a three-week hole asserts a trajectory nobody measured, and a smoothed
 * curve asserts specific values. Breaking the line leaves the gap visible, which
 * is the honest rendering and matches how the rest of the app treats absence.
 */
export function splitOnGaps(points: readonly SeriesPoint[], maxGapDays: number): SeriesPoint[][] {
  if (points.length === 0) return []

  const sorted = [...points].sort((left, right) => left.x - right.x)
  const segments: SeriesPoint[][] = []
  let current: SeriesPoint[] = []

  for (const point of sorted) {
    const previous = current.at(-1)
    if (previous !== undefined && point.x - previous.x > maxGapDays) {
      segments.push(current)
      current = []
    }
    current.push(point)
  }

  if (current.length > 0) segments.push(current)
  return segments
}

/** The smallest and largest y in a series, or undefined when it is empty. */
export function extent(points: readonly SeriesPoint[]): [number, number] | undefined {
  if (points.length === 0) return undefined

  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY
  for (const point of points) {
    if (point.y < min) min = point.y
    if (point.y > max) max = point.y
  }
  return [min, max]
}
